// TableEditor.js
import React, {useMemo, useState, useCallback} from "react";
import {Box, IconButton, Tooltip, Typography, Menu, MenuItem} from "@mui/material";
import {useTranslation} from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import {DndContext, closestCenter, PointerSensor, useSensor, useSensors} from "@dnd-kit/core";
import {
    arrayMove,
    SortableContext,
    horizontalListSortingStrategy,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import SortableItem from "./SortableItem";
import {useSortable} from "@dnd-kit/sortable";
import {CSS} from "@dnd-kit/utilities";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";

/**
 * API
 * <TableEditor
 *   table={{ label, table_header: string[], rows: string[][] }}
 *   renderCell={(fieldKey, value) => ReactNode} // uses your existing EditableField machinery
 *   onChange={(patch) => void}                 // call with { table_header?, rows?, label? }
 * />
 */
export default function TableEditor({table, renderCell, onChange, showToolbar = true}) {
    const {t} = useTranslation();
    const sensors = useSensors(
        useSensor(PointerSensor, {activationConstraint: {distance: 6}})
    );

    // Context menu (rows)
    const [rowMenu, setRowMenu] = useState({
        open: false,
        index: null,
        mouseX: null,
        mouseY: null,
    });
    const openRowMenu = (e, ri) => {
        e.preventDefault();
        e.stopPropagation();
        const {clientX, clientY} = e.nativeEvent ?? e;
        setRowMenu({
            open: true,
            index: ri,
            mouseX: clientX ?? 0,
            mouseY: clientY ?? 0,
        });
    };
    const closeRowMenu = () =>
        setRowMenu({open: false, index: null, mouseX: null, mouseY: null});


    const header = useMemo(() => Array.isArray(table.table_header) ? [...table.table_header] : [], [table.table_header]);
    const rows = useMemo(() => Array.isArray(table.rows) ? table.rows.map(r => [...r]) : [], [table.rows]);
    const rowHeader = useMemo(
        () => Array.isArray(table.row_header) ? [...table.row_header] : [],
        [table.row_header]
    );

    // Selection
    const [selectedCols, setSelectedCols] = useState(new Set());
    const [selectedRows, setSelectedRows] = useState(new Set());

    const colsCount = Math.max(
        header.length,
        rows.reduce((m, r) => Math.max(m, r.length), 0)
    );

    // then use it here
    const colIds = useMemo(
        () => Array.from({length: colsCount}, (_, i) => `col_${i}`),
        [colsCount]
    );

    const rowIds = useMemo(() => rows.map((_, i) => `row_${i}`), [rows]);

    const commit = useCallback((nextHeader, nextRows, nextRowHeader) => {
        onChange({
            table_header: nextHeader ?? header,
            rows: nextRows ?? rows,
            row_header: nextRowHeader ?? rowHeader,
        });
    }, [header, rows, rowHeader, onChange]);

    // --- Column mutations ---
    const addCol = useCallback((index) => {
        const nextHeader = [...header];
        nextHeader.splice(index, 0, t("table.column_n", {n: index + 1}));
        const nextRows = rows.map(r => {
            const copy = [...r];
            copy.splice(index, 0, "");
            return copy;
        });
        commit(nextHeader, nextRows);
    }, [header, rows, commit, t]);

    const deleteSelectedCols = useCallback(() => {
        if (selectedCols.size === 0) return;
        const indices = [...selectedCols].sort((a, b) => b - a); // delete right->left
        const nextHeader = [...header];
        const nextRows = rows.map(r => [...r]);

        indices.forEach(i => {
            if (i >= 0 && i < nextHeader.length) nextHeader.splice(i, 1);
            nextRows.forEach(r => {
                if (i >= 0 && i < r.length) r.splice(i, 1);
            });
        });
        commit(nextHeader, nextRows);
        setSelectedCols(new Set());
    }, [header, rows, selectedCols, commit]);

    const moveCol = useCallback((direction) => {
        if (selectedCols.size !== 1) return;
        const i = [...selectedCols][0];
        const j = i + (direction === "left" ? -1 : 1);
        if (j < 0 || j >= colsCount) return;

        // Move header
        const nextHeader = [...header];
        if (i < nextHeader.length && j < nextHeader.length) {
            const h = nextHeader.splice(i, 1)[0];
            nextHeader.splice(j, 0, h);
        } else if (i >= nextHeader.length || j >= nextHeader.length) {
            // fill header length if needed
            while (nextHeader.length < colsCount) nextHeader.push(t("table.column_n", {n: nextHeader.length + 1}));
            const h = nextHeader.splice(i, 1)[0];
            nextHeader.splice(j, 0, h);
        }

        // Move each row’s cell
        const nextRows = rows.map(r => {
            const copy = [...r];
            while (copy.length < colsCount) copy.push("");
            const cell = copy.splice(i, 1)[0];
            copy.splice(j, 0, cell);
            return copy;
        });

        commit(nextHeader, nextRows);
    }, [selectedCols, header, rows, colsCount, commit]);

    // DnD for columns (optional—enables free reorder by drag)
    const onDragEndCols = useCallback(({active, over}) => {
        if (!over || active.id === over.id) return;
        const from = colIds.indexOf(active.id);
        const to = colIds.indexOf(over.id);
        if (from === -1 || to === -1) return;

        const nextHeader = arrayMove(header, from, to);
        const nextRows = rows.map(r => arrayMove([...r, ...Array(Math.max(0, colsCount - r.length)).fill("")], from, to).slice(0, Math.max(r.length, colsCount)));
        commit(nextHeader, nextRows);
    }, [colIds, header, rows, colsCount, commit]);

    // --- Row mutations ---
    const addRow = useCallback((index) => {
        const nextRows = [...rows];
        const newRow = Array(colsCount).fill("");
        nextRows.splice(index, 0, newRow);

        const nextRowHeader = [...rowHeader];
        nextRowHeader.splice(index, 0, String(index + 1)); // or "" if you prefer blank

        commit(null, nextRows, nextRowHeader);

    }, [rows, colsCount, rowHeader, commit]);

    const deleteSelectedRows = useCallback(() => {
        if (selectedRows.size === 0) return;
        const indices = [...selectedRows].sort((a, b) => b - a);

        const nextRows = [...rows];
        const nextRowHeader = [...rowHeader];

        indices.forEach(i => {
            if (i >= 0 && i < nextRows.length) nextRows.splice(i, 1);
            if (i >= 0 && i < nextRowHeader.length) nextRowHeader.splice(i, 1);
        });

        commit(null, nextRows, nextRowHeader);
        setSelectedRows(new Set());
    }, [rows, rowHeader, selectedRows, commit]);


    const moveRow = useCallback((direction) => {
        if (selectedRows.size !== 1) return;
        const i = [...selectedRows][0];
        const j = i + (direction === "up" ? -1 : 1);
        if (j < 0 || j >= rows.length) return;

        const nextRows = arrayMove(rows, i, j);
        const nextRowHeader = arrayMove(rowHeader, i, j);

        commit(null, nextRows, nextRowHeader);
    }, [rows, rowHeader, selectedRows, commit]);

    const onDragEndRows = useCallback(({active, over}) => {
        if (!over || active.id === over.id) return;
        const from = rowIds.indexOf(active.id);
        const to = rowIds.indexOf(over.id);
        if (from === -1 || to === -1) return;

        const nextRows = arrayMove(rows, from, to);
        const nextRowHeader = arrayMove(rowHeader, from, to);

        commit(null, nextRows, nextRowHeader);
    }, [rowIds, rows, rowHeader, commit]);


    // Selection handlers
    const toggleCol = (ci) => {
        setSelectedCols(prev => {
            const next = new Set(prev);
            next.has(ci) ? next.delete(ci) : next.add(ci);
            return next;
        });
        setSelectedRows(new Set()); // clear row selection when selecting columns
    };

    const toggleRow = (ri) => {
        setSelectedRows(prev => {
            const next = new Set(prev);
            next.has(ri) ? next.delete(ri) : next.add(ri);
            return next;
        });
        setSelectedCols(new Set()); // clear col selection when selecting rows
    };

    const isColSelected = (ci) => selectedCols.has(ci);
    const isRowSelected = (ri) => selectedRows.has(ri);

    // Grid: only the data columns; the row handle lives outside the grid.
    const gridTemplate = `repeat(${colsCount}, minmax(0, 1fr))`;


    const [menu, setMenu] = useState({
        open: false,
        kind: /** 'col' | 'row' */ null,
        index: null,
        mouseX: null,
        mouseY: null,
    });
    const openMenu = (e, kind, index) => {
        e.preventDefault();
        e.stopPropagation();
        const {clientX, clientY} = e.nativeEvent ?? e;
        setMenu({
            open: true,
            kind,
            index,
            mouseX: clientX ?? 0,
            mouseY: clientY ?? 0,
        });
    };
    const closeMenu = () => setMenu({open: false, kind: null, index: null, mouseX: null, mouseY: null});

    // Single-index row helpers
    const deleteRowAt = useCallback((ri) => {
        if (ri == null) return;
        const nextRows = rows.filter((_, i) => i !== ri);
        const nextRowHeader = rowHeader.filter((_, i) => i !== ri);
        commit(null, nextRows, nextRowHeader);
    }, [rows, rowHeader, commit]);

    const moveRowAt = useCallback((ri, direction) => {
        const j = ri + (direction === "up" ? -1 : 1);
        if (ri < 0 || j < 0 || ri >= rows.length || j >= rows.length) return;
        commit(null, arrayMove(rows, ri, j), arrayMove(rowHeader, ri, j));
    }, [rows, rowHeader, commit]);

    // Single-index column helpers
    const moveColAt = useCallback((ci, direction) => {
        const j = ci + (direction === "left" ? -1 : 1);
        if (j < 0 || j >= colsCount) return;
        const nextHeader = arrayMove([...header, ...Array(Math.max(0, colsCount - header.length)).fill("")], ci, j).slice(0, colsCount);
        const nextRows = rows.map(r =>
            arrayMove([...r, ...Array(Math.max(0, colsCount - r.length)).fill("")], ci, j).slice(0, Math.max(r.length, colsCount))
        );
        commit(nextHeader, nextRows);
    }, [header, rows, colsCount, commit]);

    function SortableColumnHeader({id, selected, onSelect, onDelete, children}) {
        const {attributes, listeners, setNodeRef, transform, transition, isDragging} = useSortable({id});

        return (
            <Box
                ref={setNodeRef}
                sx={{
                    transform: CSS.Transform.toString(transform),
                    transition,
                    zIndex: isDragging ? 2 : 1,
                    // this box is the grid cell; keep sizing stable
                    boxSizing: "border-box",
                    minWidth: 0,
                }}
            >
                {/* top handle strip */}
                <Box
                    {...attributes}
                    {...listeners}
                    onContextMenu={(e) => openMenu(e, 'col', colIds.indexOf(id))}
                    onMouseDown={(e) => e.stopPropagation()} // don’t trigger select/edit
                    sx={{
                        height: 10,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: isDragging ? "grabbing" : "grab",
                        userSelect: "none",
                        opacity: 0.6,
                    }}
                >
                    <DragIndicatorIcon sx={{fontSize: 12, lineHeight: 1}}/>
                </Box>

                {/* header content below the handle */}
                {/* header content below the handle */}
                <Box
                    onMouseDown={(e) => e.stopPropagation()} // let the TextField get the clicks; no DnD here
                    sx={{
                        p: 0.5,
                        minHeight: 32,
                        display: "flex",
                        alignItems: "center",
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        bgcolor: "background.paper",
                        cursor: "text",
                        boxSizing: "border-box",
                        minWidth: 0,
                        fontSize: 12,
                        lineHeight: 1.3,
                        position: "relative",
                    }}
                >
                    {children}

                </Box>
            </Box>
        );
    }

    const deleteColAt = useCallback((ci) => {
        if (ci == null) return;
        const nextHeader = [...header];
        const nextRows = rows.map(r => [...r]);
        if (ci >= 0 && ci < nextHeader.length) nextHeader.splice(ci, 1);
        nextRows.forEach(r => {
            if (ci >= 0 && ci < r.length) r.splice(ci, 1);
        });
        commit(nextHeader, nextRows);
    }, [header, rows, commit]);


    return (
        <Box display="flex" flexDirection="column" gap={1}
             sx={{
                 // If you ever make the body scrollable separately, this keeps header aligned.
                 // (No harm if not used.)
                 scrollbarGutter: 'stable',
             }}>            {/* Toolbar */}
            {/* Toolbar — compact & “chip” style */}
            {showToolbar && (
                <Box
                    display="flex"
                    alignItems="center"
                    gap={0.5}
                    sx={{
                        px: 1,
                        py: 0.5,
                        borderRadius: 2,
                        bgcolor: "action.hover",
                        width: "fit-content",
                    }}
                >
                    <Typography variant="caption" sx={{mr: 0.5, opacity: 0.8}}>
                        {t("table.title")}
                    </Typography>
                    {/* Column actions */}
                    <Tooltip title={t("table.add_column_left")}>
          <span>
            <IconButton size="small"
                        onClick={() => addCol(Math.min(...(selectedCols.size ? [...selectedCols] : [header.length])))}>
              <AddIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>

                    <Tooltip title={t("table.move_column_left")}>
          <span>
            <IconButton size="small" onClick={() => moveCol("left")} disabled={selectedCols.size !== 1}>
              <ArrowBackIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>
                    <Tooltip title={t("table.move_column_right")}>
          <span>
            <IconButton size="small" onClick={() => moveCol("right")} disabled={selectedCols.size !== 1}>
              <ArrowForwardIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>

                    {/* Row actions */}
                    <Box sx={{ml: 1}}/>
                    <Tooltip title={t("table.add_row_above")}>
          <span>
            <IconButton size="small"
                        onClick={() => addRow(Math.min(...(selectedRows.size ? [...selectedRows] : [rows.length])))}>
              <AddIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>

                    <Tooltip title={t("table.move_row_up")}>
          <span>
            <IconButton size="small" onClick={() => moveRow("up")} disabled={selectedRows.size !== 1}>
              <ArrowUpwardIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>
                    <Tooltip title={t("table.move_row_down")}>
          <span>
            <IconButton size="small" onClick={() => moveRow("down")} disabled={selectedRows.size !== 1}>
              <ArrowDownwardIcon fontSize="inherit"/>
            </IconButton>
          </span>
                    </Tooltip>
                </Box>
            )}

            {/* Table */}
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEndCols}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndCols}>
                    <SortableContext items={colIds} strategy={horizontalListSortingStrategy}>
                        {/* Header */}
                        <Box
                            display="grid"
                            gridTemplateColumns={gridTemplate}
                            gap={0.25} // NEW: tighter grid spacing
                            sx={{
                                position: "sticky", // NEW: sticky header
                                top: 0,
                                zIndex: 3,
                                pt: 0.5,
                                pb: 0.5,
                                bgcolor: "background.paper",
                                borderBottom: "1px solid",
                                borderColor: "divider",
                            }}
                        >
                            {Array.from({length: colsCount}).map((_, ci) => {
                                const headerValue = header?.[ci] ?? t("table.column_n", {n: ci + 1});
                                return (
                                    <SortableColumnHeader key={colIds[ci]} id={colIds[ci]}>

                                        {renderCell(`header_${ci}`, headerValue, true, true)}
                                    </SortableColumnHeader>
                                );
                            })}
                        </Box>
                    </SortableContext>
                </DndContext>
            </DndContext>
            {/* Body */}
            <DndContext collisionDetection={closestCenter} onDragEnd={onDragEndRows}>
                <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEndRows}>
                    <SortableContext items={rowIds} strategy={verticalListSortingStrategy}>
                        {rows.map((row, ri) => (
                            <SortableItem
                                key={rowIds[ri]}
                                id={rowIds[ri]}
                                onContextMenu={(e) => openMenu(e, 'row', ri)}   // ← right-click the handle
                            >
                                <Box display="grid" gridTemplateColumns={gridTemplate} gap={0.25} alignItems="stretch">
                                    {/* Data cells */}
                                    {Array.from({length: colsCount}).map((_, ci) => {
                                        const cellVal = row?.[ci] ?? "";
                                        const selected = false; // no sticky highlight; selection is gone
                                        return (
                                            <Box
                                                key={`cell_${ri}_${ci}`}
                                                onMouseDown={(e) => e.stopPropagation()}
                                                sx={{
                                                    p: 0.5,
                                                    minHeight: 32,
                                                    display: "flex",
                                                    alignItems: "center",
                                                    border: "1px solid",
                                                    borderColor: "divider",
                                                    borderRadius: 1,
                                                    bgcolor: "background.paper",
                                                    boxSizing: 'border-box',
                                                    minWidth: 0,
                                                    "&:hover": {bgcolor: "action.hover"}, // NEW
                                                    "&:focus-within": {
                                                        outline: "2px solid",
                                                        outlineColor: "primary.light",
                                                        outlineOffset: 0,
                                                    }, // NEW: keyboard visible
                                                }}
                                            >
                                                {renderCell(`cell_${ri}_${ci}`, cellVal, true, false)}
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </SortableItem>
                        ))}
                    </SortableContext>
                </DndContext>
            </DndContext>
            <Menu
                open={menu.open}
                onClose={closeMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    menu.open && menu.mouseY !== null && menu.mouseX !== null
                        ? {top: menu.mouseY, left: menu.mouseX}
                        : undefined
                }
                transformOrigin={{horizontal: 'left', vertical: 'top'}}
                PaperProps={{sx: {zIndex: 2000}}}
            >
                {menu.kind === 'col' && (
                    <>
                        <MenuItem onClick={() => {
                            addCol(menu.index);
                            closeMenu();
                        }}>{t("table.add_column_left")}</MenuItem>
                        <MenuItem onClick={() => {
                            addCol(menu.index + 1);
                            closeMenu();
                        }}>{t("table.add_column_right")}</MenuItem>
                        <MenuItem onClick={() => {
                            deleteColAt(menu.index);
                            closeMenu();
                        }}>{t("table.delete_column")}</MenuItem>
                        <MenuItem onClick={() => {
                            moveColAt(menu.index, 'left');
                            closeMenu();
                        }}>{t("table.move_left")}</MenuItem>
                        <MenuItem onClick={() => {
                            moveColAt(menu.index, 'right');
                            closeMenu();
                        }}>{t("table.move_right")}</MenuItem>
                    </>
                )}
                {menu.kind === 'row' && (
                    <>
                        <MenuItem onClick={() => {
                            addRow(menu.index);
                            closeMenu();
                        }}>{t("table.add_row_above")}</MenuItem>
                        <MenuItem onClick={() => {
                            addRow(menu.index + 1);
                            closeMenu();
                        }}>{t("table.add_row_below")}</MenuItem>
                        <MenuItem onClick={() => {
                            deleteRowAt(menu.index);
                            closeMenu();
                        }}>{t("table.delete_row")}</MenuItem>
                        <MenuItem onClick={() => {
                            moveRowAt(menu.index, 'up');
                            closeMenu();
                        }}>{t("table.move_up")}</MenuItem>
                        <MenuItem onClick={() => {
                            moveRowAt(menu.index, 'down');
                            closeMenu();
                        }}>{t("table.move_down")}</MenuItem>
                    </>
                )}
            </Menu>
            <Menu
                open={rowMenu.open}
                onClose={closeRowMenu}
                anchorReference="anchorPosition"
                anchorPosition={
                    rowMenu.open && rowMenu.mouseY != null && rowMenu.mouseX != null
                        ? {top: rowMenu.mouseY, left: rowMenu.mouseX}
                        : undefined
                }
                transformOrigin={{horizontal: "left", vertical: "top"}}
                PaperProps={{sx: {zIndex: 2000}}}
            >
                <MenuItem onClick={() => {
                    addRow(rowMenu.index);
                    closeRowMenu();
                }}>{t("table.add_row_above")}</MenuItem>
                <MenuItem onClick={() => {
                    addRow(rowMenu.index + 1);
                    closeRowMenu();
                }}>{t("table.add_row_below")}</MenuItem>
                <MenuItem onClick={() => {
                    // delete row at index
                    const ri = rowMenu.index;
                    if (ri != null) {
                        const nextRows = rows.filter((_, i) => i !== ri);
                        const nextRowHeader = (rowHeader ?? []).filter((_, i) => i !== ri);
                        commit(null, nextRows, nextRowHeader);
                    }
                    closeRowMenu();
                }}>{t("table.delete_row")}</MenuItem>
                <MenuItem onClick={() => {
                    const ri = rowMenu.index, j = ri - 1;
                    if (ri > 0) {
                        commit(null, arrayMove(rows, ri, j), arrayMove(rowHeader ?? [], ri, j));
                    }
                    closeRowMenu();
                }}>{t("table.move_up")}</MenuItem>
                <MenuItem onClick={() => {
                    const ri = rowMenu.index, j = ri + 1;
                    if (ri != null && j < rows.length) {
                        commit(null, arrayMove(rows, ri, j), arrayMove(rowHeader ?? [], ri, j));
                    }
                    closeRowMenu();
                }}>{t("table.move_down")}</MenuItem>
            </Menu>
        </Box>


    );
}
